import { Groups, IosShare, SearchOffOutlined as SearchOffIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { FilterMenuOption } from "@/components/common/FilterMenuButton";
import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { DataFreshnessFooter } from "@/components/common/DataFreshnessFooter";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import {
  ComparisonPageHeaderShell,
  InsightsHeaderSelectorField,
  InsightsHeaderSelectorRow,
} from "@/components/common/ReportingPageHeaderShell";
import ReportingWindowSelector from "@/components/common/ReportingWindowSelector";
import { SortSelectorControl } from "@/components/common/SortSelectorControl";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useDateRangeBadge } from "@/hooks/useDateRangeBadge";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import i18n from "@/i18n/i18n";
import { ComparisonBreakdownPanel } from "@/pages/store-insights/shared/ComparisonBreakdownPanel";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { STORE_INSIGHTS_GROUP_COMPARISON_SELECTION_SESSION_KEY } from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsDeviceTypeFilter } from "@/pages/store-insights/shared/useStoreInsightsDeviceTypeFilter";
import { useStoreInsightsMultiStoreSelection } from "@/pages/store-insights/shared/useStoreInsightsMultiStoreSelection";
import { useStoreInsightsSharedDateWindow } from "@/pages/store-insights/shared/useStoreInsightsSharedDateWindow";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { Store } from "@/services/features/StoreService";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import {
  formatCompactNumber as formatCompactNumberLocalized,
  formatPercentFromWhole,
} from "@/utils/number";
import { getBrowserTimezone } from "@/utils/storeTime";
import { formatDurationShort } from "@/utils/time";

const currencyMutedStyle: React.CSSProperties = {
  color: "grey",
  filter: "grayscale(100%)",
};

type MetricValueProps = {
  value: React.ReactNode;
  secondary?: React.ReactNode;
};

const MetricValue: React.FC<MetricValueProps> = ({ value, secondary }) => (
  <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
    <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
      {value}
    </Box>
    {secondary && (
      <Box component="span" sx={{ color: "text.secondary", fontSize: "0.6875rem", mt: 0.5 }}>
        {secondary}
      </Box>
    )}
  </Box>
);

const GroupComparison: React.FC = () => {
  const { t } = useTranslation();
  const [tableLoading, setTableLoading] = useState(false);
  const { period, customRange, currentDateRange, onPeriodChange, onCustomRangeChange } =
    useStoreInsightsSharedDateWindow({
      defaultPeriod: "yesterday",
    });
  const {
    user,
    isCraveUser,
    loading,
    selectionLoading,
    customers,
    selectedCustomerIds,
    selectedGroups,
    selectedStores,
    groups,
    groupCounts,
    onCustomerChange,
    onGroupsChange,
  } = useStoreInsightsMultiStoreSelection({
    sessionKey: STORE_INSIGHTS_GROUP_COMPARISON_SELECTION_SESSION_KEY,
    includeUngroupedGroups: false,
    fallbackSingleStoreSessionKey: null,
    defaultToAllGroupsWhenUninitialized: true,
    syncSharedSelection: false,
  });
  const { deviceType, onDeviceTypeChange } = useStoreInsightsDeviceTypeFilter({
    selectionSession: {
      kind: "multi",
      key: STORE_INSIGHTS_GROUP_COMPARISON_SELECTION_SESSION_KEY,
    },
    selectionReady: !loading && !selectionLoading,
    preserveAllInUrl: true,
  });
  const { freshnessFooterIcon, freshnessFooterText, freshnessFooterTooltip } = useDateRangeBadge({
    period,
  });
  const deviceTypeLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("filters.deviceTypes.central");
      case "IN_AISLE":
        return t("filters.deviceTypes.inAisle");
      case "MOBILE_PICKUP":
        return t("filters.deviceTypes.mobile");
      case "FITTING_ROOM":
        return t("filters.deviceTypes.fittingRoom");
      case "all":
      default:
        return t("filters.deviceTypes.all");
    }
  }, [deviceType, t]);
  return (
    <div className="group-comparison-page">
      <ComparisonPageHeaderShell
        title={t("groupComparison.groupComparison.title")}
        gradientTitle
        icon={<Groups />}
        loading={loading || tableLoading}
        headerControls={
          <InsightsHeaderSelectorRow className="group-comparison-print-hide" sx={{ gap: 2 }}>
            {isCraveUser && (
              <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 160, lg: 300 } }}>
                <StoreMultiSelect
                  options={customers}
                  value={customers.filter((c) => selectedCustomerIds.includes(c.id))}
                  className="w-full"
                  minDropdownWidth={300}
                  dropdownAlign="right"
                  onChange={(next) => {
                    onCustomerChange(next.map((c) => c.id));
                  }}
                  label={t("groupComparison.groupComparison.fields.customers")}
                  loading={loading}
                  getOptionLabel={(c) => c.name}
                  getOptionKey={(c) => c.id}
                  emptyText={t("groupComparison.groupComparison.empty.noCustomers")}
                />
              </InsightsHeaderSelectorField>
            )}

            <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 160, lg: 300 } }}>
              <StoreMultiSelect
                className="w-full font-bold"
                minDropdownWidth={300}
                dropdownAlign="right"
                rowHeight="default"
                options={groups}
                value={selectedGroups}
                currentUserRole={user?.role}
                isDemo={(g) => g.toLowerCase().includes("demo")}
                onChange={onGroupsChange}
                label={t("groupComparison.groupComparison.fields.groups")}
                loading={loading}
                getOptionLabel={(g) => g}
                getOptionKey={(g) => g}
                getOptionSecondary={(g) => {
                  const n = groupCounts[g] ?? 0;
                  return t("groupComparison.groupComparison.groupStoresCount", {
                    count: n,
                  });
                }}
                emptyText={t("groupComparison.groupComparison.empty.noGroups")}
              />
            </InsightsHeaderSelectorField>
          </InsightsHeaderSelectorRow>
        }
        secondaryControls={
          <div className="flex flex-col gap-4 mb-4">
            <div className="w-full">
              <ReportingWindowSelector
                className="group-comparison-date-filter"
                value={period}
                onChange={onPeriodChange}
                showRangeText
                dateRange={currentDateRange}
                customRange={customRange}
                onCustomRangeChange={onCustomRangeChange}
                showDeviceFilter
                selectedDeviceType={deviceType}
                onDeviceTypeChange={onDeviceTypeChange}
              />
            </div>
          </div>
        }
        printMeta={
          <div className="group-comparison-print-only group-comparison-print-meta">
            <span>
              {selectedStores.length === 1
                ? selectedStores[0]?.name
                : t("groupComparison.groupComparison.selectedStores", {
                    count: selectedStores.length,
                  })}
            </span>
            <span>
              {t("filters.deviceType")}: {deviceTypeLabel}
            </span>
          </div>
        }
      />
      <div className="p-5 pt-0">
        <StoreMetricsBreakdownTable
          onLoadingChange={setTableLoading}
          selectionLoading={selectionLoading}
          selectedStores={selectedStores}
          dateRange={currentDateRange}
          deviceType={deviceType}
        />
      </div>
      <DataFreshnessFooter
        icon={freshnessFooterIcon}
        text={freshnessFooterText}
        tooltip={freshnessFooterTooltip}
      />
    </div>
  );
};

function StoreMetricsBreakdownTable({
  onLoadingChange,
  selectionLoading,
  selectedStores,
  dateRange,
  deviceType,
}: {
  onLoadingChange?: (loading: boolean) => void;
  selectionLoading: boolean;
  selectedStores: Store[];
  dateRange: DateRange | undefined;
  deviceType: DeviceType;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXlUp = useMediaQuery(theme.breakpoints.up("xl"));
  const browserTimezone = getBrowserTimezone();
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const analytics = AnalyticsServiceV2.getInstance();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  type Row = {
    // For group comparison: use group name as identifier/display
    storeName: string; // group name
    storeCount?: number; // number of stores in the group
    storesInGroupLabel?: string; // formatted label like "3 stores"
    // Key levers
    triedOnValue?: string; // total value tried on (currency)
    triedOnSub?: string; // submetric to mirror analytics card subtitle
    unitsPerSession?: string;
    upsSub?: string; // submetric for units per session (avg session value)
    missedPct?: string;
    missedSub?: string; // submetric for missed requests (total missed value in item requests)
    avgResponseTime?: string;
    // Breakdown
    namedShoppers?: string;
    dwellTime?: string;
    customerSatisfaction?: string;
    customerSatisfactionSub?: string; // sublabel: number of shoppers who left reviews
    digitalEngagement?: string;
    // legacy totals kept for potential future use (not displayed in table)
    totalRequests?: string;
    totalRequestsValue?: string;
    frSessions?: string;
    frSessionsValue?: string;
  };
  const [rows, setRows] = useState<Row[]>([]);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const localizedTablePagination = useLocalizedTablePagination();
  const showExperienceRating = deviceType === "all";

  const sessionsHeaderLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("storeComparison.fields.centralSessions");
      case "IN_AISLE":
        return t("storeComparison.fields.inAisleSessions");
      case "MOBILE_PICKUP":
        return t("storeComparison.fields.mobileSessions");
      case "FITTING_ROOM":
        return t("storeComparison.fields.fittingRoomSessions");
      case "all":
      default:
        return t("storeComparison.fields.sessions");
    }
  }, [deviceType, t]);

  type SortKey = Exclude<keyof Row, "frSessionsValue" | "totalRequestsValue">;
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const getSortDirectionLabel = (direction: "asc" | "desc") =>
    direction === "asc"
      ? t("groupComparison.groupComparison.sortDirection.ascending")
      : t("groupComparison.groupComparison.sortDirection.descending");

  const getDefaultSortDirection = (key: SortKey) => (key === "storeName" ? "asc" : "desc");

  const getNextSortDirectionLabel = (key: SortKey) =>
    getSortDirectionLabel(
      sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : getDefaultSortDirection(key),
    );

  const getSortAriaLabel = (key: SortKey, metric: string) =>
    t("groupComparison.groupComparison.sortByMetric", {
      metric,
      direction: getNextSortDirectionLabel(key),
    });

  type MobileSortKey = SortKey | "";
  const mobileSortOptions = useMemo<FilterMenuOption<MobileSortKey>[]>(
    () => [
      {
        value: "",
        label: t("groupComparison.groupComparison.options.none"),
        showDividerAfter: true,
      },
      {
        value: "frSessions",
        label: sessionsHeaderLabel,
      },
      {
        value: "unitsPerSession",
        label: t("groupComparison.groupComparison.fields.unitsPerSession"),
      },
      {
        value: "missedPct",
        label: t("groupComparison.groupComparison.fields.missedRequests"),
      },
      {
        value: "avgResponseTime",
        label: t("groupComparison.groupComparison.fields.avgResponseTime"),
      },
      {
        value: "namedShoppers",
        label: t("groupComparison.groupComparison.fields.namedShoppers"),
      },
      {
        value: "dwellTime",
        label: t("groupComparison.groupComparison.fields.dwellTime"),
      },
      ...(showExperienceRating
        ? [
            {
              value: "customerSatisfaction" as const,
              label: t("groupComparison.groupComparison.fields.experienceRating"),
            },
          ]
        : []),
    ],
    [sessionsHeaderLabel, showExperienceRating, t],
  );

  const desktopLeafColumns = useMemo<Array<{ key: SortKey; label: string; className?: string }>>(
    () => [
      {
        key: "frSessions",
        label: sessionsHeaderLabel,
        className: "group-comparison-key-levers-start",
      },
      {
        key: "unitsPerSession",
        label: t("groupComparison.groupComparison.fields.unitsPerSession"),
      },
      {
        key: "totalRequests",
        label: t("groupComparison.groupComparison.fields.totalRequests"),
      },
      {
        key: "missedPct",
        label: t("groupComparison.groupComparison.fields.missedRequests"),
      },
      {
        key: "avgResponseTime",
        label: t("groupComparison.groupComparison.fields.avgResponseTime"),
      },
      {
        key: "namedShoppers",
        label: t("groupComparison.groupComparison.fields.namedShoppers"),
        className: "group-comparison-breakdown-start",
      },
      {
        key: "dwellTime",
        label: t("groupComparison.groupComparison.fields.dwellTime"),
      },
      ...(showExperienceRating
        ? [
            {
              key: "customerSatisfaction" as const,
              label: t("groupComparison.groupComparison.fields.experienceRating"),
            },
          ]
        : []),
    ],
    [sessionsHeaderLabel, showExperienceRating, t],
  );

  useEffect(() => {
    if (!showExperienceRating && sortKey === "customerSatisfaction") {
      setSortKey(null);
    }
  }, [showExperienceRating, sortKey]);

  const parseComparable = (key: SortKey, value: string | undefined): number | null => {
    if (!value || value === "--") return null;
    switch (key) {
      case "unitsPerSession":
      case "customerSatisfaction":
        return isNaN(parseFloat(value)) ? null : parseFloat(value);
      case "totalRequests":
      case "namedShoppers":
      case "frSessions": {
        // Supports compact formats like "3.2K", "1.1M" as well as plain integers
        const trimmed = value.trim();
        const compactMatch = trimmed.match(/^([0-9]+(?:\.[0-9]+)?)\s*([kKmMbB])$/);
        if (compactMatch) {
          const num = parseFloat(compactMatch[1]);
          if (isNaN(num)) return null;
          const suffix = compactMatch[2].toLowerCase();
          const mult = suffix === "k" ? 1_000 : suffix === "m" ? 1_000_000 : 1_000_000_000;
          return num * mult;
        }
        // Fallback: remove any non-digits (like commas) and parse
        const digits = trimmed.replace(/[^0-9.-]/g, "");
        const asInt = parseFloat(digits);
        return isNaN(asInt) ? null : asInt;
      }
      case "avgResponseTime": {
        // formats like "1m 20s" or "45s"
        try {
          const m = value.match(/(?:(\d+)m)?\s*(\d+)s/);
          if (m) {
            const minutes = m[1] ? parseInt(m[1], 10) : 0;
            const seconds = parseInt(m[2], 10);
            return minutes * 60 + seconds;
          }
        } catch {}
        return null;
      }
      case "missedPct":
      case "digitalEngagement": {
        // formats like "12%"
        const num = parseFloat(value.replace("%", ""));
        return isNaN(num) ? null : num;
      }
      case "triedOnValue": {
        // currency strings like "$1,234.56" or "£1.3K"
        const hasK = /k/i.test(value);
        const numeric = parseFloat(value.replace(/[^0-9.-]/g, ""));
        if (isNaN(numeric)) return null;
        return hasK ? numeric * 1000 : numeric;
      }
      case "dwellTime": {
        // formats like "1m 20s" or "45s"
        try {
          const m = value.match(/(?:(\d+)m)?\s*(\d+)s/);
          if (m) {
            const minutes = m[1] ? parseInt(m[1], 10) : 0;
            const seconds = parseInt(m[2], 10);
            return minutes * 60 + seconds;
          }
        } catch {}
        return null;
      }
      default:
        return null;
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sortKey === "storeName") {
        const comparison = a.storeName.localeCompare(b.storeName, currentLanguage);
        return sortDir === "asc" ? comparison : -comparison;
      }

      const av = parseComparable(sortKey, a[sortKey] as string | undefined);
      const bv = parseComparable(sortKey, b[sortKey] as string | undefined);
      // Place nulls ("--") at the end regardless of direction
      const aNull = av === null;
      const bNull = bv === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      const cmp = av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [currentLanguage, rows, sortKey, sortDir]);

  const requestSort = (key: SortKey) => {
    setPage(0);
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(getDefaultSortDirection(key));
    }
  };

  const maxPage = Math.max(Math.ceil(sortedRows.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const paginatedRows = sortedRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    setPage((current) => Math.min(current, maxPage));
  }, [maxPage]);

  const renderPagination = (compact = false) => (
    <TablePagination
      component="div"
      count={sortedRows.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      sx={compact ? { "& .MuiToolbar-root": { minHeight: 0, px: 0 } } : undefined}
      {...localizedTablePagination}
    />
  );

  const handleExport = () => {
    if (sortedRows.length === 0) return;

    setExporting(true);
    try {
      downloadCsv(
        [
          [
            t("groupComparison.groupComparison.fields.group"),
            sessionsHeaderLabel,
            t("groupComparison.groupComparison.fields.unitsPerSession"),
            t("groupComparison.groupComparison.fields.totalRequests"),
            t("groupComparison.groupComparison.fields.missedRequests"),
            t("groupComparison.groupComparison.fields.avgResponseTime"),
            t("groupComparison.groupComparison.fields.namedShoppers"),
            t("groupComparison.groupComparison.fields.dwellTime"),
            ...(showExperienceRating
              ? [t("groupComparison.groupComparison.fields.experienceRating")]
              : []),
          ],
          ...sortedRows.map((row) => [
            row.storeName,
            row.frSessions ?? "--",
            row.unitsPerSession ?? "--",
            row.totalRequests ?? "--",
            row.missedPct ?? "--",
            row.avgResponseTime ?? "--",
            row.namedShoppers ?? "--",
            row.dwellTime ?? "--",
            ...(showExperienceRating ? [row.customerSatisfaction ?? "--"] : []),
          ]),
        ],
        "group-comparison.csv",
      );
    } finally {
      setExporting(false);
    }
  };

  const headerActions = (
    <>
      {renderPagination(true)}
      <Tooltip title={t("storeComparison.actions.exportCsv")}>
        <span>
          <IconButton
            size="small"
            onClick={handleExport}
            disabled={loading || sortedRows.length === 0 || exporting}
            sx={{ color: "rgb(59 130 246)" }}
          >
            <IosShare fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const formatSeconds = (seconds: number): string => {
    if (!isFinite(seconds) || seconds <= 0) return "--";
    return formatDurationShort(seconds, i18n.resolvedLanguage || i18n.language);
  };

  // Unified time format: always return "Xm Ys" (or "Ys" if < 1 minute)
  const formatMillisToMin = (millis: number): string => {
    if (!isFinite(millis) || millis <= 0) return "--";
    return formatDurationShort(millis / 1000, i18n.resolvedLanguage || i18n.language);
  };

  const formatCompactNumber = (num: number): string => {
    return formatCompactNumberLocalized(num, currentLanguage, 1);
  };

  // Render numeric rating with stars out of 5
  const renderStarRating = (val: string | undefined): React.ReactNode => {
    if (!val || val === "--") return "--";
    const rating = parseFloat(val);
    if (isNaN(rating)) return val;
    const full = Math.min(5, Math.max(0, Math.round(rating)));
    const fullStars = "★".repeat(full);
    const emptyStars = "☆".repeat(5 - full);
    return (
      <span
        className="inline-flex items-center gap-1"
        aria-label={t("groupComparison.groupComparison.ratingOutOfFive", {
          rating,
        })}
        title={t("groupComparison.groupComparison.ratingTooltip", { rating })}
      >
        <span>{val}</span>
        <span className="text-yellow-500">
          {fullStars}
          {emptyStars}
        </span>
      </span>
    );
  };

  useEffect(() => {
    if (selectionLoading) {
      setLoading(true);
      return;
    }

    // Group stores by their group name
    const grouped: Record<string, Store[]> = {};
    for (const s of selectedStores) {
      const g = (s as any).group as string | undefined;
      if (!g) continue;
      if (!grouped[g]) grouped[g] = [];
      grouped[g].push(s);
    }

    const groupNames = Object.keys(grouped);
    if (!groupNames.length || !dateRange) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const startDate = storePerformanceService.formatDate(dateRange.start, browserTimezone);
    const endDate = storePerformanceService.formatDate(dateRange.end, browserTimezone);

    let cancelled = false;
    setRows([]);

    (async () => {
      try {
        // Batch all storeIds across groups into single sets per endpoint
        const allStoreIds = Array.from(
          new Set(groupNames.flatMap((g) => grouped[g].map((s) => s.id))),
        );

        const [sessionsResp, requestsResp, missedResp, feedbackResp] = await Promise.all([
          analytics.getSessionsStatsByStore({
            storeIds: allStoreIds,
            startDate,
            endDate,
            deviceType,
          }),
          analytics.getRequestStatsByStore({
            storeIds: allStoreIds,
            startDate,
            endDate,
            deviceType,
          }),
          analytics.getRequestStatsByStore({
            storeIds: allStoreIds,
            startDate,
            endDate,
            requestStatus: "MISSED",
            deviceType,
          }),
          showExperienceRating
            ? analytics.getShopperFeedbackAverageByStore({
                storeIds: allStoreIds,
                startDate,
                endDate,
              })
            : Promise.resolve(null),
        ]);

        const sessionsByStore = sessionsResp?.data || {};
        const requestsByStore = requestsResp?.data || {};
        const missedByStore = missedResp?.data || {};
        const feedbackByStore = feedbackResp?.data || {};

        const computedRows: Row[] = [];

        for (const groupName of groupNames) {
          if (cancelled) break;
          const ids = grouped[groupName].map((s) => s.id);

          // Aggregate per group from per-store maps
          let totalSessions = 0;
          let totalSessionsIncludingNoItems = 0;
          let totalItems = 0;
          let totalDwellMs = 0;
          let totalDwellMsIncludingNoItems = 0;
          let named = 0;
          const engaged = 0;
          let totalSessionValue: number | null = 0;

          let totalReq = 0;
          let totalTimeSec = 0;
          let totalRequestValue: number | null = 0;
          let missedReq = 0;
          let missedValue: number | null = 0;

          let shopperRatings = 0;
          let totalShopperRating = 0;

          for (const id of ids) {
            const key = String(id);
            const s = sessionsByStore[key];
            if (s) {
              totalSessions += s.totalSessions ?? 0;
              totalSessionsIncludingNoItems += s.totalSessionsIncludingNoItems ?? 0;
              totalItems += s.totalItems ?? 0;
              totalDwellMs += s.totalDwellMillisecond ?? 0;
              totalDwellMsIncludingNoItems += s.totalDwellMillisecondIncludingNoItems ?? 0;
              named += s.numberOfNamedShoppers ?? 0;
              const tsv = s.totalSessionValue;
              if (tsv != null) totalSessionValue = (totalSessionValue ?? 0) + tsv;
            }
            const r = requestsByStore[key];
            if (r) {
              totalReq += r.totalRequests ?? 0;
              totalTimeSec += r.totalTimeTakenSeconds ?? 0;
              const trv = r.totalRequestValue;
              if (trv != null) totalRequestValue = (totalRequestValue ?? 0) + trv;
            }
            const m = missedByStore[key];
            if (m) {
              missedReq += m.totalRequests ?? 0;
              const mv = m.totalRequestValue;
              if (mv != null) missedValue = (missedValue ?? 0) + mv;
            }
            const f = feedbackByStore[key];
            if (f) {
              shopperRatings += f.numberOfShopperRating ?? 0;
              totalShopperRating += f.totalShopperRating ?? 0;
            }
          }

          // Normalize nulls back if sums remained null
          if (totalSessionValue === 0) totalSessionValue = null;
          if (totalRequestValue === 0) totalRequestValue = null;
          if (missedValue === 0) missedValue = null;

          const sessionCount =
            deviceType === "IN_AISLE" ? totalSessionsIncludingNoItems : totalSessions;
          const dwellTotalMs =
            deviceType === "IN_AISLE" ? totalDwellMsIncludingNoItems : totalDwellMs;

          const unitsPerSession = sessionCount > 0 ? (totalItems / sessionCount).toFixed(2) : "--";
          const avgRespTime = totalReq > 0 ? formatSeconds(totalTimeSec / totalReq) : "--";
          const pct = (n: number) =>
            totalReq > 0
              ? formatPercentFromWhole((n / totalReq) * 100, i18n.resolvedLanguage || i18n.language)
              : "--";
          const dwellStr = sessionCount > 0 ? formatMillisToMin(dwellTotalMs / sessionCount) : "--";
          const csat =
            shopperRatings > 0 && totalShopperRating > 0
              ? (totalShopperRating / shopperRatings).toFixed(1)
              : "--";
          const adjustedEngaged = Math.round(engaged * 0.8);
          const engagementPct =
            totalSessions > 0
              ? formatPercentFromWhole(
                  (adjustedEngaged / totalSessions) * 100,
                  i18n.resolvedLanguage || i18n.language,
                )
              : "--";

          const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(grouped[groupName]);
          const triedOnSub =
            sessionCount > 0
              ? t("groupComparison.groupComparison.triedOnSummary", {
                  count: sessionCount,
                })
              : undefined;
          const upsSub =
            sessionCount > 0 && totalSessionValue != null
              ? t("groupComparison.groupComparison.averageSessionValue", {
                  value: formatCurrencyWithNoDecimals(
                    Math.round(totalSessionValue / sessionCount),
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  ),
                })
              : undefined;
          const missedSub =
            totalReq > 0 && missedValue != null
              ? t("groupComparison.groupComparison.itemRequestsSummary", {
                  count: missedReq.toLocaleString(),
                  value: formatCurrencyWithNoDecimals(
                    missedValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  ),
                })
              : undefined;
          const storeCount = grouped[groupName]?.length ?? 0;
          const storesInGroupLabel = t("groupComparison.groupComparison.groupStoresCount", {
            count: storeCount,
          });
          const newRow: Row = {
            storeName: groupName,
            storeCount,
            storesInGroupLabel,
            triedOnValue:
              totalSessionValue != null
                ? formatCurrencyWithNoDecimals(
                    totalSessionValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  )
                : "--",
            triedOnSub,
            unitsPerSession,
            upsSub,
            missedPct: pct(missedReq),
            missedSub,
            avgResponseTime: avgRespTime,
            namedShoppers: named ? named.toString() : "--",
            dwellTime: dwellStr,
            customerSatisfaction: csat,
            customerSatisfactionSub:
              shopperRatings > 0
                ? t("groupComparison.groupComparison.reviewsFromShoppers", {
                    count: shopperRatings,
                  })
                : undefined,
            digitalEngagement: engagementPct,
            totalRequests: totalReq ? totalReq.toString() : "--",
            totalRequestsValue:
              totalRequestValue != null
                ? formatCurrencyWithNoDecimals(
                    totalRequestValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  )
                : "--",
            frSessions: sessionCount ? formatCompactNumber(sessionCount) : "--",
            frSessionsValue:
              totalSessionValue != null
                ? formatCurrencyWithNoDecimals(
                    totalSessionValue,
                    currencyCode as CurrencyCode,
                    currencySymbol,
                  )
                : "--",
          };

          computedRows.push(newRow);
        }

        if (!cancelled) {
          setRows(computedRows);
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error("Failed to load group breakdown", e);
          setError(e?.message || t("groupComparison.groupComparison.errors.failedLoadBreakdown"));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    // react to selected store set (affects which groups) and date range
    selectedStores.map((s) => (s as any).group ?? "").join("|"),
    selectedStores.map((s) => s.currencySymbol ?? "").join("|"),
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    currentLanguage,
    deviceType,
    selectionLoading,
    showExperienceRating,
  ]);

  return (
    <div className="mt-8">
      {error && <div className="text-red-500 text-center py-2">{error}</div>}
      <>
        {/* Large-screen view: keep table for scanability */}
        <ComparisonBreakdownPanel
          className="group-comparison-desktop-table"
          sx={{
            display: { xs: "none", lg: "block" },
          }}
          title={t("groupComparison.groupComparison.title")}
          actions={headerActions}
          footer={renderPagination()}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Table
              sx={{
                ...tableInCardSx,
                minWidth: 1100,
                "& .MuiTableHead-root .MuiTableRow-root:first-of-type .MuiTableCell-root": {
                  padding: (theme) => theme.spacing(1, 1.5),
                },
                "& .group-comparison-section-header": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textAlign: "center",
                  textTransform: "uppercase",
                },
                "& .group-comparison-breakdown-header, & .group-comparison-breakdown-start": {
                  borderInlineStart: "1px solid",
                  borderColor: "divider",
                },
                "& .group-comparison-group-header, & .group-comparison-group-cell": {
                  borderInlineEnd: "1px solid",
                  borderColor: "divider",
                },
                "& .group-comparison-group-header": {
                  insetInlineStart: 0,
                  position: "sticky",
                  zIndex: 2,
                },
                "& .group-comparison-group-cell": {
                  insetInlineStart: 0,
                  position: "sticky",
                  zIndex: 1,
                },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd) .group-comparison-group-cell":
                  {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#111827" : "#FFFFFF",
                  },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .group-comparison-group-cell":
                  {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#1F2937" : "#F9FAFB",
                  },
                "& .MuiTableHead-root .MuiTableRow-root:nth-of-type(2) .MuiTableCell-root": {
                  borderTop: 0,
                },
                "& .MuiTableHead-root .MuiTableRow-root:nth-of-type(2) .MuiTableCell-root:first-of-type":
                  {
                    borderInlineStart: 0,
                  },
                "& .MuiTableBody-root .MuiTableCell-root": {
                  borderBottom: 0,
                  padding: (theme) => theme.spacing(1, 1.5),
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    component="th"
                    className="group-comparison-group-header"
                    rowSpan={2}
                    scope="col"
                    sortDirection={sortKey === "storeName" ? sortDir : false}
                    sx={{ fontWeight: 600, maxWidth: 220, minWidth: 220, width: 220 }}
                  >
                    <TableSortLabel
                      active={sortKey === "storeName"}
                      direction={sortKey === "storeName" ? sortDir : "asc"}
                      onClick={() => requestSort("storeName")}
                      aria-label={getSortAriaLabel(
                        "storeName",
                        t("groupComparison.groupComparison.fields.group"),
                      )}
                    >
                      <Box component="span">
                        {t("groupComparison.groupComparison.fields.group")}
                      </Box>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    component="th"
                    className="group-comparison-key-levers-header group-comparison-section-header"
                    colSpan={5}
                    scope="colgroup"
                  >
                    {t("groupComparison.groupComparison.sections.keyLevers")}
                  </TableCell>
                  <TableCell
                    component="th"
                    className="group-comparison-breakdown-header group-comparison-section-header"
                    colSpan={showExperienceRating ? 3 : 2}
                    scope="colgroup"
                  >
                    {t("groupComparison.groupComparison.sections.breakdown")}
                  </TableCell>
                </TableRow>
                <TableRow>
                  {desktopLeafColumns.map(({ key, label, className }) => {
                    const active = sortKey === key;

                    return (
                      <TableCell
                        key={key}
                        component="th"
                        className={className}
                        scope="col"
                        sortDirection={active ? sortDir : false}
                      >
                        <TableSortLabel
                          active={active}
                          direction={active ? sortDir : "asc"}
                          onClick={() => requestSort(key)}
                          aria-label={getSortAriaLabel(key, label)}
                        >
                          <Box component="span">{label}</Box>
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeletonRows columnCount={desktopLeafColumns.length + 1} />
                ) : (
                  paginatedRows.map((r) => (
                    <TableRow key={r.storeName}>
                      <TableCell
                        className="group-comparison-group-cell"
                        sx={{ whiteSpace: isXlUp ? "nowrap" : "normal" }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                          <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
                            {r.storeName}
                          </Box>
                          {r.storesInGroupLabel && (
                            <Box
                              component="span"
                              sx={{ color: "text.secondary", fontSize: "0.6875rem", mt: 0.5 }}
                            >
                              {r.storesInGroupLabel}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className="group-comparison-key-levers-start">
                        <MetricValue
                          value={r.frSessions ?? "--"}
                          secondary={
                            r.frSessionsValue
                              ? t("groupComparison.groupComparison.triedOnValue", {
                                  value: r.frSessionsValue,
                                })
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <MetricValue value={r.unitsPerSession ?? "--"} secondary={r.upsSub} />
                      </TableCell>
                      <TableCell>
                        <MetricValue
                          value={r.totalRequests ?? "--"}
                          secondary={
                            r.totalRequestsValue && r.totalRequestsValue !== "--"
                              ? t("groupComparison.groupComparison.itemRequestsValue", {
                                  value: r.totalRequestsValue,
                                })
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <MetricValue value={r.missedPct ?? "--"} secondary={r.missedSub} />
                      </TableCell>
                      <TableCell>
                        <MetricValue value={r.avgResponseTime ?? "--"} />
                      </TableCell>
                      <TableCell className="group-comparison-breakdown-start">
                        {r.namedShoppers ?? "--"}
                      </TableCell>
                      <TableCell>{r.dwellTime ?? "--"}</TableCell>
                      {showExperienceRating && (
                        <TableCell>
                          <MetricValue
                            value={renderStarRating(r.customerSatisfaction)}
                            secondary={r.customerSatisfactionSub}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
                {!loading && sortedRows.length === 0 && (
                  <TableRow className="table-empty-state">
                    <TableCell
                      align="center"
                      colSpan={desktopLeafColumns.length + 1}
                      sx={{ py: 5 }}
                    >
                      <AdminEmptyState
                        icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
                        text={t("groupComparison.groupComparison.empty.noDataForSelection")}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </ComparisonBreakdownPanel>

        {/* Small/medium-screen view: stack into cards with label-value pairs */}
        <ComparisonBreakdownPanel
          className="group-comparison-mobile-cards space-y-3"
          sx={{ display: { xs: "block", lg: "none" } }}
          title={t("groupComparison.groupComparison.title")}
          actions={headerActions}
          footer={renderPagination()}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <SortSelectorControl
              value={sortKey ?? ""}
              options={mobileSortOptions}
              onChange={(value) => {
                setPage(0);
                if (!value) {
                  setSortKey(null);
                  return;
                }

                setSortKey(value);
                setSortDir("desc");
              }}
              direction={sortDir}
              onDirectionChange={(direction) => {
                setPage(0);
                setSortDir(direction);
              }}
              label={t("groupComparison.groupComparison.actions.sortBy")}
              menuTitle={t("groupComparison.groupComparison.actions.sortBy")}
              directionAriaLabel={t("groupComparison.groupComparison.aria.toggleSortDirection", {
                direction: getSortDirectionLabel(sortDir === "asc" ? "desc" : "asc"),
              })}
              directionTitle={
                !sortKey
                  ? t("groupComparison.groupComparison.hints.selectMetricToSort")
                  : t("groupComparison.groupComparison.hints.direction", {
                      direction: getSortDirectionLabel(sortDir),
                    })
              }
              directionDisabled={!sortKey}
            />
          </Box>
          {paginatedRows.map((r) => (
            <div
              key={r.storeName}
              className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm"
              role="group"
              aria-label={t("groupComparison.groupComparison.aria.metricsForGroup", {
                groupName: r.storeName,
              })}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex flex-col">
                  <div className="font-bold text-gray-900 text-base truncate">{r.storeName}</div>
                  {r.storesInGroupLabel && (
                    <div className="text-[11px] text-gray-500 mt-0.5">{r.storesInGroupLabel}</div>
                  )}
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
                    {t("groupComparison.groupComparison.sections.keyLevers")}
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-2 bg-slate-50 rounded-md p-2">
                    <div className="flex flex-col">
                      <dt className="text-gray-600">{sessionsHeaderLabel}</dt>
                      <dd className="text-gray-900 font-medium">{r.frSessions ?? "--"}</dd>
                      {r.frSessionsValue && (
                        <div
                          className="text-[11px] text-gray-500 mt-0.5"
                          style={currencyMutedStyle}
                        >
                          {t("groupComparison.groupComparison.triedOnValue", {
                            value: r.frSessionsValue,
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <dt className="text-gray-600">
                        {t("groupComparison.groupComparison.fields.unitsPerSession")}
                      </dt>
                      <dd className="text-gray-900 font-medium">{r.unitsPerSession ?? "--"}</dd>
                      {r.upsSub && (
                        <div
                          className="text-[11px] text-gray-500 mt-0.5"
                          style={currencyMutedStyle}
                        >
                          {r.upsSub}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <dt className="text-gray-600">
                        {t("groupComparison.groupComparison.fields.missedRequests")}
                      </dt>
                      <dd className="text-gray-900 font-medium">{r.missedPct ?? "--"}</dd>
                      {r.missedSub && (
                        <div
                          className="text-[11px] text-gray-500 mt-0.5"
                          style={currencyMutedStyle}
                        >
                          {r.missedSub}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <dt className="text-gray-600">
                        {t("groupComparison.groupComparison.fields.avgResponseTime")}
                      </dt>
                      <dd className="text-gray-900 font-medium">{r.avgResponseTime ?? "--"}</dd>
                    </div>
                  </div>
                </div>

                <div className="col-span-2 my-2 border-t border-gray-200"></div>
                <div className="col-span-2 text-xs uppercase tracking-wide text-gray-500">
                  {t("groupComparison.groupComparison.sections.breakdown")}
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500">
                    {t("groupComparison.groupComparison.fields.namedShoppers")}
                  </dt>
                  <dd className="text-gray-900 font-medium">{r.namedShoppers ?? "--"}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500">
                    {t("groupComparison.groupComparison.fields.dwellTime")}
                  </dt>
                  <dd className="text-gray-900 font-medium">{r.dwellTime ?? "--"}</dd>
                </div>
                {showExperienceRating && (
                  <div className="flex flex-col">
                    <dt className="text-gray-500">
                      {t("groupComparison.groupComparison.fields.experienceRating")}
                    </dt>
                    <dd className="text-gray-900 font-medium">
                      {renderStarRating(r.customerSatisfaction)}
                    </dd>
                    {r.customerSatisfactionSub && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {r.customerSatisfactionSub}
                      </div>
                    )}
                  </div>
                )}
              </dl>
            </div>
          ))}
          {!loading && sortedRows.length === 0 && (
            <Box sx={{ py: 2 }}>
              <AdminEmptyState
                icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
                text={t("groupComparison.groupComparison.empty.noDataForSelection")}
              />
            </Box>
          )}
        </ComparisonBreakdownPanel>
      </>
    </div>
  );
}

export default GroupComparison;
